import firebase from 'firebase/compat/app';
import { db } from './config';
import { SpeedCompetitionData, getDefaultData, DEFAULT_COMPETITORS, Competitor, QualSlot, FinalsMatch } from '../utils/speedCompetition';

/** New subcollection path: competitions/{competitionId}/speed/config */
function docRef(competitionId: string): firebase.firestore.DocumentReference {
  return db.collection('competitions').doc(competitionId).collection('speed').doc('config');
}

/** Legacy path used for auto-migration of bk2026 */
const LEGACY_DOC = () => db.collection('speed-competition').doc('bk2026');

/** Merge raw Firestore data with defaults so new fields are never undefined. */
function mergeWithDefaults(raw: Record<string, unknown>): SpeedCompetitionData {
  const defaults = getDefaultData();

  // Use stored competitors if present (even if empty for a new competition).
  // Fall back to DEFAULT_COMPETITORS only if the field is absent (pre-migration docs).
  const competitors: Competitor[] = Array.isArray(raw.competitors)
    ? (raw.competitors as Competitor[])
    : DEFAULT_COMPETITORS.map(c => ({ ...c }));

  // Always use default qual PAIRINGS (correct athletes + timeslots).
  // Restore any run results that were stored for matching time ranges so
  // previously-entered times are not lost where the timeslot matches.
  const restoreResults = (
    defaultSlots: QualSlot[],
    stored: unknown,
  ): QualSlot[] => {
    if (!Array.isArray(stored)) return defaultSlots;
    const resultMap = new Map<string, Pick<QualSlot, 'leftResult' | 'rightResult'>>();
    (stored as QualSlot[]).forEach(s => {
      if (s?.timeRange) {
        resultMap.set(s.timeRange, {
          leftResult:  s.leftResult,
          rightResult: s.rightResult,
        });
      }
    });
    return defaultSlots.map(s => {
      const r = resultMap.get(s.timeRange);
      return r ? { ...s, ...r } : s;
    });
  };

  return {
    ...defaults,
    competitors,
    qualRound1: restoreResults(defaults.qualRound1, raw.qualRound1),
    qualRound2: restoreResults(defaults.qualRound2, raw.qualRound2),
    mensQuarterFinals: Array.isArray(raw.mensQuarterFinals) ? raw.mensQuarterFinals as FinalsMatch[] : defaults.mensQuarterFinals,
    mensSemiFinals:    Array.isArray(raw.mensSemiFinals)    ? raw.mensSemiFinals    as FinalsMatch[] : defaults.mensSemiFinals,
    mensFinals:        Array.isArray(raw.mensFinals)        ? raw.mensFinals        as FinalsMatch[] : defaults.mensFinals,
    womensSemiFinals:  Array.isArray(raw.womensSemiFinals)  ? raw.womensSemiFinals  as FinalsMatch[] : defaults.womensSemiFinals,
    womensFinals:      Array.isArray(raw.womensFinals)      ? raw.womensFinals      as FinalsMatch[] : defaults.womensFinals,
  };
}

/** Load once (used on admin mount to seed defaults if doc missing). */
export async function loadSpeedData(competitionId: string): Promise<SpeedCompetitionData> {
  const ref = docRef(competitionId);
  let snap = await ref.get();

  if (!snap.exists && competitionId === 'bk2026') {
    // Auto-migration: copy legacy doc and embed DEFAULT_COMPETITORS so they
    // survive the new mergeWithDefaults logic which respects stored competitors.
    const legacySnap = await LEGACY_DOC().get();
    const base = legacySnap.exists ? legacySnap.data()! : {};
    await ref.set({ ...base, competitors: DEFAULT_COMPETITORS });
    snap = await ref.get();
  }

  if (snap.exists) {
    return mergeWithDefaults(snap.data() as Record<string, unknown>);
  }
  // Brand-new competition: start with empty competitors
  const empty: SpeedCompetitionData = { ...getDefaultData(), competitors: [], qualRound1: [], qualRound2: [] };
  await ref.set(empty);
  return empty;
}

/** Overwrite the whole document. */
export async function saveSpeedData(
  competitionId: string,
  data: SpeedCompetitionData,
): Promise<void> {
  await docRef(competitionId).set(data);
}

/** Real-time listener. Returns an unsubscribe function. */
export function subscribeSpeedData(
  competitionId: string,
  onData: (data: SpeedCompetitionData) => void,
  onError?: (err: Error) => void,
): () => void {
  return docRef(competitionId).onSnapshot(
    snap => {
      if (snap.exists) {
        onData(mergeWithDefaults(snap.data() as Record<string, unknown>));
      }
    },
    err => {
      console.error('Firestore speed-competition listener error:', err);
      onError?.(err);
    },
  );
}
